import type { MeasurementAnswerInput } from '@/src/dal/measurement-dal'
import type { MeasurementDefinitionDetail } from '@/src/dal/session-dal'
import type { QueryClient } from '@tanstack/react-query'
import {
  createMeasurement,
  saveMeasurementRecording,
} from '@/src/dal/measurement-dal'
import { measurementQueryOptions } from '@/src/queries/measurement-queries'
import { queryKeys } from '@/src/queries/query-keys'

import { measurementRequiresRecording } from '../components/VoiceAssessmentFlow.shared'

function inferRecordingContentType(uri: string): string {
  const normalizedUri = uri.toLowerCase().split('?')[0] ?? ''
  if (normalizedUri.endsWith('.m4a') || normalizedUri.endsWith('.mp4')) {
    return 'audio/mp4'
  }
  if (normalizedUri.endsWith('.caf')) {
    return 'audio/x-caf'
  }
  if (normalizedUri.endsWith('.wav')) {
    return 'audio/wav'
  }
  if (normalizedUri.endsWith('.webm')) {
    return 'audio/webm'
  }
  if (normalizedUri.endsWith('.3gp')) {
    return 'audio/3gpp'
  }
  return 'application/octet-stream'
}

function getRecordingContentType({
  confirmedRecordingBlob,
  confirmedRecordingUri,
}: {
  confirmedRecordingBlob: Blob | null
  confirmedRecordingUri: string | null
}) {
  const blobType = confirmedRecordingBlob?.type.trim()
  if (blobType != null && blobType.length > 0) {
    return blobType
  }
  return confirmedRecordingUri != null
    ? inferRecordingContentType(confirmedRecordingUri)
    : null
}

export const VOICE_ASSESSMENT_UPLOAD_ERROR = {
  missingRecordings: 'VOICE_ASSESSMENT_MISSING_RECORDINGS',
  authRequired: 'VOICE_ASSESSMENT_AUTH_REQUIRED',
} as const

export interface SubmitVoiceAssessmentMeasurementParams {
  queryClient: QueryClient
  /** Backend session id minted by `POST /api/session/Start`. */
  sessionId: number
  /** Current measurement definition being completed. */
  measurementDefinition: MeasurementDefinitionDetail
  /** Confirmed recording URI for native file uploads, if present. */
  confirmedRecordingUri?: string | null
  /** Confirmed recording Blob for this measurement, if present. */
  confirmedRecordingBlob?: Blob | null
  /** Dynamic question answers for this measurement. */
  measurementQuestionAnswers: MeasurementAnswerInput[]
}

/**
 * Persists one completed measurement in the active voice-assessment session:
 *
 *   POST /api/measurement                -> { measurementId }
 *   POST /api/measurement/{id}/recording (raw audio Blob, when recorded)
 *
 * Session progression and `POST /api/session/{sessionId}/End` stay in the
 * flow layer; this service only knows about the current measurement.
 */
export async function submitVoiceAssessmentMeasurement(
  params: SubmitVoiceAssessmentMeasurementParams,
): Promise<number> {
  const {
    queryClient,
    sessionId,
    measurementDefinition,
    confirmedRecordingUri = null,
    confirmedRecordingBlob = null,
    measurementQuestionAnswers,
  } = params

  if (
    measurementRequiresRecording(measurementDefinition) &&
    confirmedRecordingBlob == null &&
    confirmedRecordingUri == null
  ) {
    throw new Error(VOICE_ASSESSMENT_UPLOAD_ERROR.missingRecordings)
  }
  const contentType = getRecordingContentType({
    confirmedRecordingBlob,
    confirmedRecordingUri,
  })

  const { measurementId } = await createMeasurement({
    sessionId,
    definitionId: measurementDefinition.id,
    encoding: contentType,
    answers: measurementQuestionAnswers,
  })
  await queryClient.invalidateQueries({
    queryKey: measurementQueryOptions(measurementId).queryKey,
  })

  if (confirmedRecordingBlob != null) {
    await saveMeasurementRecording({
      measurementId,
      recording: {
        kind: 'blob',
        body: confirmedRecordingBlob,
      },
    })
  } else if (confirmedRecordingUri != null && contentType != null) {
    await saveMeasurementRecording({
      measurementId,
      recording: {
        kind: 'fileUri',
        uri: confirmedRecordingUri,
        contentType,
      },
    })
  }

  await queryClient.invalidateQueries({
    queryKey: queryKeys.backendSessions.byId(sessionId),
  })

  return measurementId
}
